import React from "react";
import { BaseCard, SVGIcon } from "../baseCard/baseCard.tsx";
import {
  getFontSizes,
  getTitleHeight,
  darkenColor,
} from "../baseCard/BaseCardUtils.ts";
import "./dotCard.css";

interface DotCardProps {
  category: string;
  title: string;
  subtitle1: string;
  value: number;
  unit: string;
  subtitle2: string;
  score: number; // 1-5
  highlight?: boolean;
}

const DotCard: React.FC<DotCardProps> = ({
  category,
  title,
  subtitle1,
  value,
  unit,
  subtitle2,
  score,
  highlight = false,
}) => {
  const fontSizes = getFontSizes(highlight);
  const titleHeight = getTitleHeight(highlight);
  const color = "var(--color-light-gray)";
  const shapeColor = darkenColor(color);

  const renderDots = () => {
    const dots = [];
    for (let i = 1; i <= 5; i++) {
      dots.push(
        <div
          key={i}
          className={`dotCard-dot ${i <= score ? "dotCard-dot-filled" : ""}`}
        />
      );
    }
    return dots;
  };

  const svgPaths = {
    topShape:
      "M20.7637 1.11044e-05C9.71799 1.24175e-05 0.76368 8.95433 0.763673 20L0.763678 97.4131C9.37694 104.498 32.5399 112.037 67.7002 90.1162C103.94 67.5225 112.736 17.7127 114.172 0L20.7637 1.11044e-05Z",
    bottomShape:
      "M206.493 109.598C206.493 120.643 197.539 129.598 186.493 129.598H0.626953C8.79625 105.382 42.9206 56.9502 114.063 56.9502C185.206 56.9502 205.326 19.2817 206.493 0.447266V109.598Z",
  };

  return (
    <BaseCard highlight={highlight} color={color} className="dotCard-card">
      <SVGIcon
        className="baseCard-icon-top-right"
        viewBox="0 0 115 105"
        fillColor={shapeColor}
        path={svgPaths.topShape}
      />

      <SVGIcon
        className="baseCard-icon-bottom-left"
        viewBox="0 0 207 130"
        fillColor={shapeColor}
        path={svgPaths.bottomShape}
      />

      <div className="baseCard-content dotCard-content">
        <div className="dotCard-header">
          <div
            className="dotCard-category"
            style={{ fontSize: fontSizes.category }}
          >
            {category.toUpperCase()}
          </div>

          <div
            className="dotCard-title"
            style={{
              fontSize: fontSizes.title,
              height: titleHeight,
            }}
          >
            {title}
          </div>

          <div
            className="baseCard-divider dotCard-divider"
            style={{
              width: "calc(100% + 2 * var(--spacing-3))",
            }}
          ></div>
        </div>

        <div className="dotCard-main-content">
          <div
            className="dotCard-subtitle1"
            style={{ fontSize: fontSizes.listItem }}
          >
            {subtitle1}{" Unit:"} {unit}
          </div>

          <div className="dotCard-value-container">
            {unit.includes("kWh") ? (
              <svg
                width="141"
                height="58"
                viewBox="0 0 141 58"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2 46V12C2 6.47715 6.47715 2 12 2H119.938C125.461 2 129.938 6.47715 129.938 12V19.7188C134.943 19.7188 139 23.7841 139 28.7891C139 33.794 134.943 37.8594 129.938 37.8594V46C129.938 51.5229 125.461 56 119.938 56H12C6.47715 56 2 51.5229 2 46Z"
                  fill="#28537D"
                />
                <path
                  d="M129.938 19.7188V12C129.938 6.47715 125.461 2 119.938 2H12C6.47715 2 2 6.47715 2 12V46C2 51.5228 6.47715 56 12 56H119.938C125.461 56 129.938 51.5229 129.938 46V37.8594M129.938 19.7188V19.7188C134.943 19.7188 139 23.7841 139 28.7891V28.7891C139 33.794 134.943 37.8594 129.938 37.8594V37.8594M129.938 19.7188V37.8594"
                  stroke="#28537D"
                  strokeWidth="4"
                  strokeLinejoin="round"
                />
                <text
                  x="66"
                  y="38"
                  textAnchor="middle"
                  fill="var(--color-green)"
                  fontSize="22"
                  fontWeight="bold"
                >
                  {value}
                </text>
              </svg>
            ) : (
              <svg
                width="141"
                height="58"
                viewBox="0 0 141 58"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g transform="translate(10.88, 0) scale(0.90625)">
                  <path
                    d="M0 54V10C0 4.47715 4.47714 0 9.99999 0H70.1097C72.7184 0 74.8332 2.11478 74.8332 4.72349C74.8332 7.33221 76.9479 9.44699 79.5566 9.44699H93.0787C93.0787 4.22956 97.3083 0 102.526 0H109.413C114.631 0 118.86 4.22956 118.86 9.44699C122.804 9.44699 126 12.6435 126 16.5865V54C126 59.5228 121.523 64 116 64H10C4.47715 64 0 59.5228 0 54Z"
                    fill="#28537D"
                  />
                </g>
                <text
                  x="66"
                  y="38"
                  textAnchor="middle"
                  fill="var(--color-green)"
                  fontSize="22"
                  fontWeight="bold"
                >
                  {value}
                </text>
              </svg>
            )}
          </div>

          <div className="dotCard-thin-divider"></div>

          <div className="dotCard-score-section">
            <div className="dotCard-score-header">
              <span
                className="dotCard-subtitle2"
                style={{ fontSize: fontSizes.listItem }} >
                {subtitle2}
              </span>
            </div>

            <div className="dotCard-dots-container">{renderDots()}</div>
          </div>
        </div>
      </div>
    </BaseCard>
  );
};

export default DotCard;
